import { ApiService } from '../utils/ApiService';
import { JournalColumnDefinition, JournalTemplate, ListValueNote } from '../types/journal';
import { MasterdataListRow, codeOfListItem, columnLetter, listItemsOf, listKeyOf, masterdataPathOf, matchFormOf } from '../utils/journalLists';
import { AccountPair, accountListOf, accountPairsOf, registerListsOf, typesOffered } from '../utils/journalAccountTypes';

/** Holds every dropdown list of the workbook, one column per company, user and list, its key in row 1. */
export const LIST_SHEET_NAME = 'Bifrost lists';
const LIST_NAME_PREFIX = 'BifrostList_';
/** The fill "Check for errors" gives a cell in error, and the only fill the add-in ever clears. */
export const ERROR_FILL = '#FFC7CE';
/** A paste larger than this is left to "Check for errors". */
const MAX_CHANGED_CELLS = 5000;
/** How long a list read fresh from the ERP counts as current, so a burst of edits asks once. */
const FRESH_READ_INTERVAL_MS = 30 * 1000;
/** The list of account types: the types it offers are the registers read for the account columns. */
const TYPE_LIST = 'accountTypes';
/**
 * The most list rows one request reads or writes. Excel on the web refuses a request or a response
 * over 5 MB, and a register of customers can run to tens of thousands: in one request, it would cost
 * the sheet every dropdown.
 */
const LIST_CHUNK_ROWS = 5000;

/** The masterdata path, asking the backend to read the list again from the ERP first. */
function freshPath(path: string): string {
  return `${path}${path.indexOf('?') < 0 ? '?' : '&'}fresh=true`;
}

interface ListColumn {
  index: number;
  col: JournalColumnDefinition;
  key: string;
}

/** A journal sheet's data rows, which start below its 1-based header row. */
interface SheetRows {
  worksheetId: string;
  headerRow: number;
  rows: number;
}

/**
 * Whose lists a refresh fetches and writes, taken when it starts: a company switch while it runs
 * must not file one company's lists under the other, nor point one company's sheet at the other's.
 */
export interface ListOwner {
  scope: string;
  headers: Record<string, string>;
}

/** A company and user as one key: whose lists these are, and whose a Journal tab was loaded for. */
export function ownerScope(companyId: string | undefined, userId: string | undefined): string {
  return `${companyId ?? ''}:${userId ?? ''}`;
}

/**
 * What a refresh got: how many lists the sheet's columns draw on, how many it was given, and how many
 * could not be read. A list the ERP has no values for is read, but gives no dropdown.
 */
export interface ListRefresh {
  expected: number;
  written: number;
  failed: number;
}

/** Lists read from the backend as dropdown items, and how many were asked for and could not be read. */
interface FetchedLists {
  lists: Map<string, string[]>;
  expected: number;
  failed: number;
}

/** A list as the list sheet holds it: the column it fills, its items, and whether its workbook name exists. */
interface HeldList {
  column: number;
  items: string[];
  named: boolean;
}

/** One of the owner's lists as dropdowns use it: its workbook name, null where the workbook has none, and its items. */
interface WorkbookList {
  name: string | null;
  items: string[];
}

/** What an edited cell needs: its code written back, the mark set or cleared — or nothing. */
interface CellChange {
  value?: string;
  mark?: 'set' | 'clear';
}

/** A coded column of the edited rows as read: its values and formulas, and its fills. */
interface ColumnRead extends ListColumn {
  range: Excel.Range;
  fills: OfficeExtension.ClientResult<Excel.CellProperties[][]>;
}

/** Account cells, from an offset of the edited rows, that need another validation: a list's name, or null for none. */
interface Rewrite {
  account: number;
  start: number;
  length: number;
  name: string | null;
}

/** A range's validation as loaded, and how many cells the range has. */
interface Judged {
  validation: Excel.DataValidation;
  cells: number;
}

/** A stretch of one column of the list sheet, read or written in one request. */
interface Stretch {
  column: number;
  /** The offset of its first item in the list. */
  start: number;
  count: number;
}

/** A column of the list sheet as read: its items, and whether its workbook name exists. */
interface ReadColumn {
  items: string[];
  named: boolean;
}

/** The codes of a list as a column matches them, against the codes as the list has them. */
type KnownCodes = (col: JournalColumnDefinition, key: string) => Map<string, string>;

/** The workbook name of the list in a column of the list sheet. */
function nameOfColumn(column: number): string {
  return `${LIST_NAME_PREFIX}${columnLetter(column)}`;
}

/**
 * What a list's workbook name refers to: exactly the list's filled cells, re-pointed at every write,
 * so a refreshed list never needs its validations redefined. A plain reference on purpose: desktop
 * Excel reads a name's formula in the user's own language, so one with functions or argument
 * separators fails to define in Norwegian Excel, and the sheet silently gets no dropdowns.
 */
function listReference(column: number, count: number): string {
  const letter = columnLetter(column);
  return `='${LIST_SHEET_NAME}'!$${letter}$2:$${letter}$${count + 1}`;
}

function listColumnsOf(columns: JournalColumnDefinition[]): ListColumn[] {
  const found: ListColumn[] = [];
  columns.forEach((col, index) => {
    const key = listKeyOf(col);
    if (key) found.push({ index, col, key });
  });
  return found;
}

/** The lists the columns draw on, each once. */
function columnListKeys(columns: JournalColumnDefinition[]): string[] {
  const keys: string[] = [];
  for (const col of columns) {
    const key = listKeyOf(col);
    if (key && masterdataPathOf(key) && keys.indexOf(key) < 0) keys.push(key);
  }
  return keys;
}

const sameColor = (a: string | null | undefined, b: string) => (a ?? '').toUpperCase() === b.toUpperCase();
/** No fill reads back as white; a user's own fill is anything else. */
const isUnfilled = (color: string | null | undefined) => !color || sameColor(color, '#FFFFFF');

/** Consecutive offsets with equal labels, as [start, length, label] runs, so each run is one range write. */
function runsBy<L>(labels: L[]): [number, number, L][] {
  const runs: [number, number, L][] = [];
  labels.forEach((label, offset) => {
    const last = runs[runs.length - 1];
    if (last && last[2] === label) last[1]++;
    else runs.push([offset, 1, label]);
  });
  return runs;
}

/** Consecutive offsets that satisfy `wanted`, as [start, length] runs. */
function runsOf(changes: CellChange[], wanted: (change: CellChange) => boolean): [number, number][] {
  return runsBy(changes.map(wanted)).filter(run => run[2]).map(([start, length]): [number, number] => [start, length]);
}

/**
 * Columns of the given lengths as stretches packed into requests of at most LIST_CHUNK_ROWS rows: the
 * short lists share a request, a long one takes several of its own.
 */
function requestsOf(lengths: [number, number][]): Stretch[][] {
  const requests: Stretch[][] = [];
  let request: Stretch[] = [];
  let rows = 0;
  for (const [column, length] of lengths) {
    for (let start = 0; start < length; start += LIST_CHUNK_ROWS) {
      const count = Math.min(LIST_CHUNK_ROWS, length - start);
      if (rows + count > LIST_CHUNK_ROWS && request.length > 0) {
        requests.push(request);
        request = [];
        rows = 0;
      }
      request.push({ column, start, count });
      rows += count;
    }
  }
  if (request.length > 0) requests.push(request);
  return requests;
}

/** Queues each request and syncs it before queueing the next. */
async function inTurn<T>(context: Excel.RequestContext, requests: T[], queue: (request: T) => void): Promise<void> {
  await requests.reduce<Promise<unknown>>((previous, request) => previous.then(() => {
    queue(request);
    return context.sync();
  }), Promise.resolve());
}

/** A dropdown on `range` from the workbook list `name`, without an alert. Runs in the caller's batch. */
function pointAt(range: Excel.Range, name: string): void {
  range.dataValidation.clear();
  range.dataValidation.rule = { list: { inCellDropDown: true, source: `=${name}` } };
  range.dataValidation.errorAlert = { showAlert: false, style: Excel.DataValidationAlertStyle.information, title: '', message: '' };
}

/**
 * Loads a range's validation: its type, and its rule only on a single cell, which cannot be mixed.
 * Excel reports cells that differ as Inconsistent or MixedCriteria, and a range's rule is read only
 * once its type says List. Runs in the caller's batch.
 */
function loadValidation(sheet: Excel.Worksheet, row: number, column: number, cells: number): Excel.DataValidation {
  const validation = sheet.getRangeByIndexes(row, column, cells, 1).dataValidation;
  validation.load(cells === 1 ? ['type', 'rule'] : ['type']);
  return validation;
}

/** Whether Excel reports one validation for every cell of a range, so its answer holds for any part of the range. */
function isUniform(validation: Excel.DataValidation): boolean {
  return validation.type !== Excel.DataValidationType.inconsistent && validation.type !== Excel.DataValidationType.mixedCriteria;
}

/**
 * Whether a range's validation, as Excel reports it, is the list `name` on every cell, or none on any
 * cell where `name` is null. A rule describes every cell only where the type says List.
 */
function validationIs(validation: Excel.DataValidation, name: string | null): boolean {
  if (name === null) return validation.type === Excel.DataValidationType.none;
  if (validation.type !== Excel.DataValidationType.list) return false;
  const source = validation.rule?.list?.source;
  return typeof source === 'string' && source.replace(/^=/, '').trim().toUpperCase() === name.toUpperCase();
}

/**
 * The name of a list the workbook holds; null where it holds none, the list's name does not exist, or
 * the row names no list. A row is never pointed at a name that does not exist.
 */
function nameOfList(lists: Map<string, WorkbookList>, key: string | null): string | null {
  return key === null ? null : lists.get(key)?.name ?? null;
}

/**
 * The type cells of the rows, for every pair with a type column, by that column: taken from `read`
 * where the column was read already, else loaded. Runs in the caller's batch.
 */
function loadTypes(
  sheet: Excel.Worksheet, pairs: AccountPair[], firstRow: number, count: number, read: Map<number, Excel.Range> = new Map()
): Map<number, Excel.Range> {
  const types = new Map<number, Excel.Range>();
  for (const pair of pairs) {
    if (pair.type === null || types.has(pair.type)) continue;
    let range = read.get(pair.type);
    if (!range) {
      range = sheet.getRangeByIndexes(firstRow, pair.type, count, 1);
      range.load('values');
    }
    types.set(pair.type, range);
  }
  return types;
}

/** The list each row's account is picked from, by the type loaded for the row. */
function accountListsOf(pair: AccountPair, types: Map<number, Excel.Range>, count: number): (string | null)[] {
  const values = pair.type === null ? [] : types.get(pair.type)?.values ?? [];
  return Array.from({ length: count }, (_unused, offset) => accountListOf(pair, values[offset]?.[0]));
}

/** Each list's codes as a column matches them, worked out once per list. */
function knownCodesOf(lists: Map<string, WorkbookList>): KnownCodes {
  const known = new Map<string, Map<string, string>>();
  return (col, key) => {
    let codes = known.get(key);
    if (!codes) {
      codes = new Map(Array.from(lists.get(key)?.items ?? [], (item): [string, string] => {
        const code = codeOfListItem(item);
        return [matchFormOf(col, code), code];
      }));
      known.set(key, codes);
    }
    return codes;
  };
}

/**
 * What each edited cell of a column needs against the list its row draws on (`keys`, by offset): a pick
 * reduced to its code, a value the list lacks marked and noted, a mark the add-in set cleared. A row
 * without a list, or whose list has no values, is not marked either way.
 */
function changesOf(read: ColumnRead, keys: (string | null)[], known: KnownCodes, lineOf: (offset: number) => number, notes: ListValueNote[]): CellChange[] {
  return read.range.values.map((row, offset): CellChange => {
    // A formula is the user's to keep, whatever it shows.
    const formula = read.range.formulas[offset]?.[0];
    if (typeof formula === 'string' && formula.charAt(0) === '=') return {};
    const key = keys[offset] ?? null;
    const codes = key === null ? undefined : known(read.col, key);
    const marks = codes !== undefined && codes.size > 0;
    const color = read.fills.value[offset]?.[0]?.format?.fill?.color;
    const ours = sameColor(color, ERROR_FILL);
    const text = String(row[0] ?? '').trim();
    if (text === '') return marks && ours ? { mark: 'clear' } : {};
    const code = codeOfListItem(text);
    const listedAs = codes?.get(matchFormOf(read.col, code));
    const value = listedAs !== undefined && listedAs !== text ? listedAs : undefined;
    if (!marks || key === null) return { value };
    if (listedAs !== undefined) return { value, mark: ours ? 'clear' : undefined };
    notes.push({ lineNumber: lineOf(offset), column: read.col.name, value: code, list: key });
    // Already marked, or the user's own fill, which is theirs to keep.
    return { value, mark: isUnfilled(color) ? 'set' : undefined };
  });
}

/** Writes a column's changes, one range per run, and answers how many writes it queued. Runs in the caller's batch. */
function writeChanges(sheet: Excel.Worksheet, index: number, firstRow: number, changes: CellChange[]): number {
  let writes = 0;
  for (const [start, length] of runsOf(changes, c => c.value !== undefined)) {
    const range = sheet.getRangeByIndexes(firstRow + start, index, length, 1);
    const values = changes.slice(start, start + length).map(c => [c.value ?? '']);
    // In the same batch as the value: a code such as 001 must not become the number 1.
    range.numberFormat = values.map(() => ['@']);
    range.values = values;
    writes++;
  }
  for (const [start, length] of runsOf(changes, c => c.mark === 'set')) {
    sheet.getRangeByIndexes(firstRow + start, index, length, 1).format.fill.color = ERROR_FILL;
    writes++;
  }
  for (const [start, length] of runsOf(changes, c => c.mark === 'clear')) {
    sheet.getRangeByIndexes(firstRow + start, index, length, 1).format.fill.clear();
    writes++;
  }
  return writes;
}

/**
 * The runs of accounts, one per run of rows of one list, whose validation is not that list's. A run is
 * judged by the validation of the whole edited range where Excel reports one for every cell, and is
 * asked about on its own where Excel reports a mix; a rule is read only where the type says List. At
 * most two requests beyond the one that loaded `whole`.
 */
async function rewritesOf(
  context: Excel.RequestContext, sheet: Excel.Worksheet, pairs: AccountPair[], whole: Excel.DataValidation[],
  types: Map<number, Excel.Range>, lists: Map<string, WorkbookList>, firstRow: number, count: number
): Promise<Rewrite[]> {
  let asked = false;
  const plans = pairs.map((pair, i) => {
    const runs = runsBy(accountListsOf(pair, types, count).map(key => nameOfList(lists, key)));
    const all: Judged | undefined = whole[i] ? { validation: whole[i], cells: count } : undefined;
    if (!all || isUniform(all.validation) || runs.length === 1) return { pair, runs, judged: runs.map(() => all) };
    asked = true;
    return { pair, runs, judged: runs.map(([start, length]): Judged | undefined => ({ validation: loadValidation(sheet, firstRow + start, pair.account, length), cells: length })) };
  });
  if (asked) await context.sync();
  const unread = new Set<Excel.DataValidation>();
  plans.forEach(({ judged }) => judged.forEach(own => {
    if (own && own.cells > 1 && own.validation.type === Excel.DataValidationType.list) unread.add(own.validation);
  }));
  unread.forEach(validation => validation.load('rule'));
  if (unread.size > 0) await context.sync();
  const rewrites: Rewrite[] = [];
  plans.forEach(({ pair, runs, judged }) => runs.forEach(([start, length, name], r) => {
    const own = judged[r];
    if (!own || !validationIs(own.validation, name)) rewrites.push({ account: pair.account, start, length, name });
  }));
  return rewrites;
}

/** Points the runs of accounts that need it at their lists, in requests of their own. */
async function repointRuns(
  context: Excel.RequestContext, sheet: Excel.Worksheet, pairs: AccountPair[], whole: Excel.DataValidation[],
  types: Map<number, Excel.Range>, lists: Map<string, WorkbookList>, firstRow: number, count: number
): Promise<void> {
  const rewrites = await rewritesOf(context, sheet, pairs, whole, types, lists, firstRow, count);
  for (const { account, start, length, name } of rewrites) {
    const range = sheet.getRangeByIndexes(firstRow + start, account, length, 1);
    if (name === null) range.dataValidation.clear();
    else pointAt(range, name);
  }
  if (rewrites.length > 0) await context.sync();
}

/**
 * Dropdown lists for the coded columns of journal sheets: codes the ERP accepts, each with its name.
 * An inline list is capped at 255 characters, which a chart of accounts or a large dimension
 * exceeds, so the lists live on a very hidden sheet and validations point at workbook names. Lists
 * belong to a company and a user — a workbook used with two companies, a demo and a live one, or
 * passed between users never offers one's codes on the other's sheets, and Dynamics 365 gives each
 * user the values their own rights allow.
 *
 * An account column beside a type column offers each row the register its type names: the chart of
 * accounts, or the customers, vendors or bank accounts. A list source must stay a plain reference to a
 * name (see `listReference`), so a row follows its type by having its own validation, set when lists
 * are written for the sheet, and when its type or its account is edited. An account column without a
 * type column has one list, the template default's, as every other coded column has one.
 */
export class JournalListService {
  private authHeaders: Record<string, string> = {};
  private scope = '';
  /** Each owner's lists as the list sheet holds them, by scope and then list key: read on first use, and left by each write. */
  private readonly held = new Map<string, Map<string, HeldList>>();
  /** When each scoped list was last read fresh from the ERP. */
  private readonly freshReads = new Map<string, number>();
  /**
   * Work on the list sheet, its writes and the reads behind `held`, runs one job at a time: two writes
   * at once would claim the same free column, and a read during a write would see lists half written.
   */
  private listWork: Promise<unknown> = Promise.resolve();
  /**
   * Sheet updates that point validations run one at a time: types read for one update and written
   * after another's would leave rows on the lists of types they no longer have.
   */
  private pointing: Promise<unknown> = Promise.resolve();

  constructor(private readonly apiService: ApiService) {}

  setAuthHeaders(headers: Record<string, string>): void {
    this.authHeaders = headers;
  }

  /** The active company and the signed-in user, whose lists are written and read. */
  setOwner(companyId: string, userId: string): void {
    this.scope = ownerScope(companyId, userId);
  }

  /** The owner as it is now, for a refresh to keep from start to finish. */
  owner(): ListOwner {
    return { scope: this.scope, headers: this.authHeaders };
  }

  /** List validation needs ExcelApi 1.8; without it sheets simply have no dropdowns. */
  supported(): boolean {
    return typeof Office !== 'undefined' && Office.context?.requirements?.isSetSupported('ExcelApi', '1.8') === true;
  }

  /**
   * Marking reads each cell's fill, which needs ExcelApi 1.9; without it a sheet keeps its dropdowns
   * and goes unmarked, and "Check for errors" still says what is wrong.
   */
  private marksSupported(): boolean {
    return typeof Office !== 'undefined' && Office.context?.requirements?.isSetSupported('ExcelApi', '1.9') === true;
  }

  /**
   * Every list a template's sheet draws on, as dropdown items, and how many were asked for and could
   * not be read. A list that could not be read, or has no values, gets no dropdown; "Check for errors"
   * still says what is wrong. The customers, vendors and bank accounts are read for the types the type
   * list offers, and for the template's defaults, while the other lists are still being read. `fresh`
   * has the backend read each list again from the ERP first, unless it did so moments ago.
   */
  async fetchLists(template: JournalTemplate, headers: Record<string, string> = this.authHeaders, fresh = false): Promise<FetchedLists> {
    const fetched: FetchedLists = { lists: new Map(), expected: 0, failed: 0 };
    if (!this.supported()) return fetched;
    const keys = columnListKeys(template.columnDefinitions);
    const reads = keys.map(key => this.fetchInto(fetched, key, headers, fresh));
    const typeRows = reads[keys.indexOf(TYPE_LIST)] ?? Promise.resolve(null);
    const registers = typeRows.then(rows => Promise.all(
      registerListsOf(template, typesOffered((rows ?? []).map(row => row.code))).map(key => this.fetchInto(fetched, key, headers, fresh))
    ));
    await Promise.all([Promise.all(reads), registers]);
    return fetched;
  }

  /** Writes the lists under `scope` and answers the workbook name of each, by list key. */
  writeLists(lists: Map<string, string[]>, scope: string = this.scope): Promise<Map<string, string>> {
    const run = this.listWork.then(() => this.writeListsNow(lists, scope));
    this.listWork = run.catch(() => undefined);
    return run;
  }

  /**
   * Re-reads the lists and points the sheet at them, so its dropdowns offer what the ERP has now;
   * `fresh` reads them from the ERP rather than the backend's copy. A list that could not be read
   * is left out and shows as `written` short of `expected`.
   */
  async refresh(template: JournalTemplate, worksheetKey: string, headerRow: number, rows: number, owner: ListOwner = this.owner(), fresh = false): Promise<ListRefresh> {
    const { lists, expected, failed } = await this.fetchLists(template, owner.headers, fresh);
    if (fresh) this.stampFresh(Array.from(lists.keys()), owner.scope);
    const names = await this.writeLists(lists, owner.scope);
    if (names.size > 0) await this.pointSheet(template, { worksheetId: worksheetKey, headerRow, rows }, owner.scope);
    return { expected, written: names.size, failed };
  }

  /**
   * The lists behind `keys` read again from the ERP, except one read fresh moments ago, written and
   * pointed at by the sheet's columns. Answers whether any list was written.
   */
  async refreshListsOf(template: JournalTemplate, keys: Set<string>, sheet: SheetRows, owner: ListOwner = this.owner()): Promise<boolean> {
    const now = Date.now();
    const due = Array.from(keys).filter(key => now - (this.freshReads.get(this.scopedKey(key, owner.scope)) ?? 0) >= FRESH_READ_INTERVAL_MS);
    if (due.length === 0) return false;
    // Stamped before the read, so an ERP that is down is not asked at every edit, and again after,
    // so the interval runs from the latest answer however long the ERP took.
    this.stampFresh(due, owner.scope);
    const fetched: FetchedLists = { lists: new Map(), expected: 0, failed: 0 };
    if (this.supported()) await Promise.all(due.map(key => this.fetchInto(fetched, key, owner.headers, true)));
    this.stampFresh(due, owner.scope);
    if (fetched.lists.size === 0) return false;
    await this.writeLists(fetched.lists, owner.scope);
    await this.pointSheet(template, sheet, owner.scope);
    return true;
  }

  /** Reads one list into `fetched`, and answers its rows; null when it could not be read. */
  private async fetchInto(fetched: FetchedLists, key: string, headers: Record<string, string>, fresh: boolean): Promise<MasterdataListRow[] | null> {
    const path = masterdataPathOf(key);
    if (!path) return null;
    fetched.expected++;
    let rows: MasterdataListRow[] | null = null;
    try {
      const response = await this.apiService.get<MasterdataListRow[]>(fresh ? freshPath(path) : path, { headers });
      if (response.success && Array.isArray(response.data)) rows = response.data;
    } catch {
      rows = null;
    }
    if (!rows) {
      fetched.failed++;
      return null;
    }
    const items = listItemsOf(key, rows);
    if (items.length > 0) fetched.lists.set(key, items);
    return rows;
  }

  private stampFresh(keys: string[], scope: string): void {
    const at = Date.now();
    keys.forEach(key => this.freshReads.set(this.scopedKey(key, scope), at));
  }

  /** The owner's dropdown items per list key, as the workbook holds them. */
  async itemsOfScope(scope: string = this.scope): Promise<Map<string, Set<string>>> {
    const found = new Map<string, Set<string>>();
    (await this.listsOfScope(scope)).forEach((list, key) => found.set(key, new Set(list.items)));
    return found;
  }

  /**
   * After an edit on a journal sheet: a picked "code – name" becomes its code, kept as text (a
   * currency also takes its list's case), and a value its list does not have gets the check's error
   * fill. An account is looked up in the list its row's type names, and looked at again when that type
   * changes. Only real changes are written — a code typed as it should be costs no write at all, so
   * Excel's undo is left alone — and only a fill the add-in set is ever cleared. Advisory: the next
   * check replaces every mark. Answers the values their lists do not have.
   *
   * With `repoint`, an account beside a type column whose type changed, or that was pasted or filled,
   * is also pointed at its row's list where its validation is another; a pass right after the sheet
   * was pointed skips it. A template without a type column is marked and nothing more.
   */
  async onCellsChanged(
    worksheetId: string, address: string, template: JournalTemplate, headerRow: number, rows: number, repoint = true
  ): Promise<ListValueNote[]> {
    if (listColumnsOf(template.columnDefinitions).length === 0 || !this.supported() || !this.marksSupported()) return [];
    const scope = this.scope;
    const mark = async (): Promise<ListValueNote[]> => {
      const lists = await this.listsOfScope(scope);
      return Excel.run(context => this.markChanged(context, { worksheetId, headerRow, rows }, address, template, lists, repoint));
    };
    const typed = accountPairsOf(template).some(pair => pair.type !== null);
    return repoint && typed ? this.inPointing(mark) : mark();
  }

  /** Marks one edited area against its lists, and with `repoint` points its accounts at their rows' lists. */
  private async markChanged(
    context: Excel.RequestContext, area: SheetRows, address: string, template: JournalTemplate, lists: Map<string, WorkbookList>, repoint: boolean
  ): Promise<ListValueNote[]> {
    const sheet = context.workbook.worksheets.getItem(area.worksheetId);
    const changed = sheet.getRange(address);
    changed.load(['rowIndex', 'columnIndex', 'rowCount', 'columnCount']);
    await context.sync();

    const firstRow = Math.max(changed.rowIndex, area.headerRow);
    const count = Math.min(changed.rowIndex + changed.rowCount, area.headerRow + area.rows) - firstRow;
    const inChange = (index: number | null) => index !== null && index >= changed.columnIndex && index < changed.columnIndex + changed.columnCount;
    const pairs = accountPairsOf(template);
    // A changed type moves its row's account to another list, where it is looked at again.
    const touched = listColumnsOf(template.columnDefinitions)
      .filter(c => inChange(c.index) || pairs.some(pair => pair.account === c.index && inChange(pair.type)));
    if (count <= 0 || touched.length === 0 || count * touched.length > MAX_CHANGED_CELLS) return [];
    // A pasted or filled account brings the validation of the cell it came from, and a changed type
    // needs another: only where a type column gives each row a list of its own.
    const repointed = repoint ? pairs.filter(pair => pair.type !== null && (inChange(pair.type) || inChange(pair.account))) : [];

    const reads = touched.map((column): ColumnRead => {
      const range = sheet.getRangeByIndexes(firstRow, column.index, count, 1);
      range.load(['values', 'formulas']);
      return { ...column, range, fills: range.getCellProperties({ format: { fill: { color: true } } }) };
    });
    const followed = pairs.filter(pair => touched.some(c => c.index === pair.account));
    const types = loadTypes(sheet, followed, firstRow, count, new Map(reads.map((read): [number, Excel.Range] => [read.index, read.range])));
    const whole = repointed.map(pair => loadValidation(sheet, firstRow, pair.account, count));
    await context.sync();

    const known = knownCodesOf(lists);
    const notes: ListValueNote[] = [];
    let writes = 0;
    for (const read of reads) {
      const pair = pairs.find(p => p.account === read.index);
      const keys = pair ? accountListsOf(pair, types, count) : read.range.values.map(() => read.key);
      const changes = changesOf(read, keys, known, offset => firstRow + offset - area.headerRow + 1, notes);
      writes += writeChanges(sheet, read.index, firstRow, changes);
    }
    if (writes > 0) await context.sync();
    // After the marks and apart from them: Excel may refuse a validation, as on a protected sheet,
    // and the marks and notes stand either way.
    if (repointed.length > 0) {
      await repointRuns(context, sheet, repointed, whole, types, lists, firstRow, count)
        .catch((error: unknown) => console.error('Failed to point accounts at the lists of their types:', error));
    }
    return notes;
  }

  /**
   * Points a sheet's list columns at the owner's lists as the workbook holds them. An account column
   * beside a type column gets each row's own list, by the row's type, consecutive rows of one list as
   * one range, and no dropdown on a row whose type names no list the workbook has a name for, where
   * another register's codes would only mislead. No alert: a typed code is accepted, a value the ERP
   * added since the list was written must not be refused, and the check is the authority.
   */
  private pointSheet(template: JournalTemplate, sheet: SheetRows, scope: string): Promise<void> {
    return this.inPointing(async () => {
      const lists = await this.listsOfScope(scope);
      const pairs = accountPairsOf(template);
      await Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getItem(sheet.worksheetId);
        const types = loadTypes(worksheet, pairs, sheet.headerRow, sheet.rows);
        if (types.size > 0) await context.sync();
        for (const { index, key } of listColumnsOf(template.columnDefinitions)) {
          const pair = pairs.find(p => p.account === index);
          if (!pair || pair.type === null) {
            const name = nameOfList(lists, pair ? accountListOf(pair, undefined) : key);
            if (name) pointAt(worksheet.getRangeByIndexes(sheet.headerRow, index, sheet.rows, 1), name);
            continue;
          }
          const names = accountListsOf(pair, types, sheet.rows).map(list => nameOfList(lists, list));
          for (const [start, length, name] of runsBy(names)) {
            const range = worksheet.getRangeByIndexes(sheet.headerRow + start, index, length, 1);
            if (name === null) range.dataValidation.clear();
            else pointAt(range, name);
          }
        }
        await context.sync();
      });
    });
  }

  private inPointing<T>(work: () => Promise<T>): Promise<T> {
    const run = this.pointing.then(work);
    this.pointing = run.catch(() => undefined);
    return run;
  }

  /** The owner's lists as the workbook holds them, by list key: each one's name and items. */
  private async listsOfScope(scope: string): Promise<Map<string, WorkbookList>> {
    const found = new Map<string, WorkbookList>();
    (await this.readLists(scope)).forEach(({ column, items, named }, key) => {
      if (items.length > 0) found.set(key, { name: named ? nameOfColumn(column) : null, items });
    });
    return found;
  }

  private scopedKey(key: string, scope: string): string {
    return `${scope}|${key}`;
  }

  /** The owner's columns among the header's keys, by list key, blank cells and other owners' columns passed over. */
  private ownedColumns(header: string[], scope: string): { key: string; column: number }[] {
    const prefix = this.scopedKey('', scope);
    const owned: { key: string; column: number }[] = [];
    header.forEach((scoped, column) => {
      const key = scoped.slice(prefix.length);
      if (scoped.indexOf(prefix) === 0 && !owned.some(o => o.key === key)) owned.push({ key, column });
    });
    return owned;
  }

  /**
   * Writes each list into its column of the list sheet — a new column, after everything on the sheet,
   * for a list it does not have yet — and points its workbook name at exactly its items. The items go
   * in requests of at most LIST_CHUNK_ROWS rows, so a register of tens of thousands never outgrows what
   * Excel on the web accepts. A column is cleared in the request that writes its first items, and its
   * name follows each request, so a write that fails partway leaves the lists after it as they were
   * and every name on exactly the items its column holds. Leaves the owner's lists in `held`.
   */
  private async writeListsNow(lists: Map<string, string[]>, scope: string): Promise<Map<string, string>> {
    const names = new Map<string, string>();
    const filled = Array.from(lists).filter(([, items]) => items.length > 0);
    if (filled.length === 0) return names;
    const before = this.held.get(scope);
    // Dropped first, so the read after a write that fails goes to the sheet.
    this.held.delete(scope);

    await Excel.run(async (context) => {
      const sheet = await this.listSheet(context);
      const { header, rows, end } = await this.readHeader(context, sheet);
      const keys = header.slice();
      let next = end;
      const written = filled.map(([key, items]) => {
        const scoped = this.scopedKey(key, scope);
        let column = keys.indexOf(scoped);
        const added = column < 0;
        if (added) {
          column = next++;
          keys[column] = scoped;
        }
        const item = context.workbook.names.getItemOrNullObject(nameOfColumn(column));
        item.load('isNullObject');
        return { key, scoped, column, items, added, item };
      });
      await context.sync();

      const byColumn = new Map(written.map(entry => [entry.column, entry]));
      const named = new Map<number, Excel.NamedItem>();
      written.forEach(({ column, item }) => { if (!item.isNullObject) named.set(column, item); });
      await inTurn(context, requestsOf(written.map(({ column, items }): [number, number] => [column, items.length])), request => {
        request.forEach(({ column, start, count }) => {
          const entry = byColumn.get(column);
          if (!entry) return;
          if (start === 0) {
            if (entry.added) sheet.getRangeByIndexes(0, column, 1, 1).values = [[entry.scoped]];
            if (rows > 1) sheet.getRangeByIndexes(1, column, rows - 1, 1).clear('Contents');
          }
          const cells = sheet.getRangeByIndexes(1 + start, column, count, 1);
          const values = entry.items.slice(start, start + count).map(item => [item]);
          // Text, so a code such as 001 is not turned into the number 1.
          cells.numberFormat = values.map(() => ['@']);
          cells.values = values;
          // Re-pointed every time: a name someone redefined would otherwise feed every dropdown from the wrong cells.
          const reference = listReference(column, start + count);
          const item = named.get(column);
          if (item) item.formula = reference;
          else named.set(column, context.workbook.names.add(nameOfColumn(column), reference));
        });
      });
      written.forEach(({ key, column }) => names.set(key, nameOfColumn(column)));
      this.held.set(scope, await this.ownerListsAfter(context, sheet, scope, keys, rows, written, before));
    });
    return names;
  }

  /**
   * The owner's lists as the list sheet holds them once a write is done: the lists it wrote as it wrote
   * them, the others as `held` had them, and any not held read from their columns.
   */
  private async ownerListsAfter(
    context: Excel.RequestContext, sheet: Excel.Worksheet, scope: string, keys: string[], rows: number,
    written: { key: string; column: number; items: string[] }[], before: Map<string, HeldList> | undefined
  ): Promise<Map<string, HeldList>> {
    const owned = this.ownedColumns(keys, scope);
    const fresh = new Map(written.map((entry): [string, HeldList] => [entry.key, { column: entry.column, items: entry.items, named: true }]));
    const kept = (key: string, column: number) => {
      const list = before?.get(key);
      return list && list.column === column ? list : undefined;
    };
    const unknown = owned.filter(({ key, column }) => !fresh.has(key) && !kept(key, column)).map(({ column }) => column);
    const read = await this.readColumns(context, sheet, unknown, rows);
    return new Map(owned.map(({ key, column }): [string, HeldList] => {
      const list = fresh.get(key) ?? kept(key, column);
      if (list) return [key, list];
      const stored = read.get(column);
      return [key, { column, items: stored?.items ?? [], named: stored?.named ?? false }];
    }));
  }

  private async listSheet(context: Excel.RequestContext): Promise<Excel.Worksheet> {
    const sheet = context.workbook.worksheets.getItemOrNullObject(LIST_SHEET_NAME);
    sheet.load(['isNullObject', 'visibility']);
    await context.sync();
    if (sheet.isNullObject) {
      const added = context.workbook.worksheets.add(LIST_SHEET_NAME);
      // Very hidden: out of the user's way, and not unhidden from Excel's menus by accident.
      added.visibility = Excel.SheetVisibility.veryHidden;
      return added;
    }
    if (sheet.visibility !== Excel.SheetVisibility.veryHidden) sheet.visibility = Excel.SheetVisibility.veryHidden;
    return sheet;
  }

  /**
   * The list sheet's header row of list keys, blank where a column has none, how many rows the sheet
   * fills, and the first column past everything on it, where a new list goes.
   */
  private async readHeader(context: Excel.RequestContext, sheet: Excel.Worksheet): Promise<{ header: string[]; rows: number; end: number }> {
    const used = sheet.getUsedRangeOrNullObject(true);
    used.load(['isNullObject', 'rowIndex', 'rowCount', 'columnIndex', 'columnCount']);
    await context.sync();
    if (used.isNullObject) return { header: [], rows: 0, end: 0 };
    const end = used.columnIndex + used.columnCount;
    const top = sheet.getRangeByIndexes(0, 0, 1, end);
    top.load('values');
    await context.sync();
    return { header: (top.values[0] ?? []).map(v => String(v ?? '')), rows: used.rowIndex + used.rowCount, end };
  }

  /** The owner's lists: read from the list sheet once no write is under way, and kept until the next write. */
  private readLists(scope: string): Promise<Map<string, HeldList>> {
    const kept = this.held.get(scope);
    if (kept) return Promise.resolve(kept);
    const run = this.listWork.then(() => this.held.get(scope) ?? this.readListsNow(scope));
    this.listWork = run.catch(() => undefined);
    return run;
  }

  private async readListsNow(scope: string): Promise<Map<string, HeldList>> {
    const lists = await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(LIST_SHEET_NAME);
      sheet.load('isNullObject');
      await context.sync();
      if (sheet.isNullObject) return new Map<string, HeldList>();
      const { header, rows } = await this.readHeader(context, sheet);
      const owned = this.ownedColumns(header, scope);
      const read = await this.readColumns(context, sheet, owned.map(({ column }) => column), rows);
      return new Map(owned.map(({ key, column }): [string, HeldList] => {
        const list = read.get(column);
        return [key, { column, items: list?.items ?? [], named: list?.named ?? false }];
      }));
    });
    this.held.set(scope, lists);
    return lists;
  }

  /**
   * The items of the given columns of the list sheet, each read only as far as it is filled, in
   * requests of at most LIST_CHUNK_ROWS rows, and whether each one's workbook name exists.
   */
  private async readColumns(context: Excel.RequestContext, sheet: Excel.Worksheet, columns: number[], rows: number): Promise<Map<number, ReadColumn>> {
    const found = new Map<number, ReadColumn>();
    if (columns.length === 0) return found;
    const probes = columns.map(column => {
      const name = context.workbook.names.getItemOrNullObject(nameOfColumn(column));
      name.load('isNullObject');
      const used = rows > 1 ? sheet.getRangeByIndexes(1, column, rows - 1, 1).getUsedRangeOrNullObject(true) : null;
      if (used) used.load(['isNullObject', 'rowIndex', 'rowCount']);
      return { column, name, used };
    });
    await context.sync();
    const lengths = probes.map(({ column, used }): [number, number] => [column, used && !used.isNullObject ? used.rowIndex + used.rowCount - 1 : 0]);
    const stretches: { column: number; range: Excel.Range }[] = [];
    await inTurn(context, requestsOf(lengths), request => request.forEach(({ column, start, count }) => {
      const range = sheet.getRangeByIndexes(1 + start, column, count, 1);
      range.load('values');
      stretches.push({ column, range });
    }));
    probes.forEach(({ column, name }) => found.set(column, { items: [], named: !name.isNullObject }));
    for (const { column, range } of stretches) {
      const list = found.get(column);
      if (!list) continue;
      range.values.forEach(row => {
        const item = String(row[0] ?? '').trim();
        if (item !== '') list.items.push(item);
      });
    }
    return found;
  }
}
